import styled from "@emotion/styled";
import { isMac } from "@/domains/platform/isMac";
import { AnimatePresence } from "motion/react";
import { motion } from "motion/react";
import { observer } from "mobx-react-lite";
import { MentionTooltipParams } from "@/components/note/types";
import { createPortal } from "react-dom";
import { ZIndex } from "@/domains/design/constants";
import { useEffect } from "react";

type TProps = {
  mentionTooltipParams?: MentionTooltipParams;
  resetMentionTooltipParams: () => void;
};

export const MentionTooltip = observer(
  ({ mentionTooltipParams, resetMentionTooltipParams }: TProps) => {
    const { visible, top, left, itemId } = mentionTooltipParams ?? {};

    useHideTooltipOnScroll({
      mentionTooltipParams,
      resetMentionTooltipParams,
    });

    const tooltipContent = (
      <AnimatePresence>
        {visible && (
          <motion.div
            key={`mention-tooltip-${itemId}`}
            initial={{ opacity: 0, transform: "translate(-50%, -90%)" }}
            animate={{ opacity: 1, transform: "translate(-50%, -100%)" }}
            exit={{ opacity: 0, transform: "translate(-50%, -90%)" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              position: "fixed",
              top: `${top}px`,
              left: `${left}px`,
              pointerEvents: "none",
              zIndex: ZIndex.MentionsMenu,
            }}
          >
            <Content>{isMac() ? "Cmd-Click" : "Ctrl-Click"} to open in different pane</Content>
          </motion.div>
        )}
      </AnimatePresence>
    );

    return createPortal(tooltipContent, document.body);
  }
);

const Content = styled.div(({ theme }) => ({
  background: theme.colors.surface.tooltip,
  color: theme.colors.text.onAccent,
  fontSize: theme.fontSizes["text-xs"],
  padding: `${theme.spacing["1"]} ${theme.spacing["2"]}`,
  borderRadius: theme.borderRadius.medium,
  boxShadow: theme.shadows.surface,
  userSelect: "none",
}));

const useHideTooltipOnScroll = ({
  mentionTooltipParams,
  resetMentionTooltipParams,
}: {
  mentionTooltipParams?: MentionTooltipParams;
  resetMentionTooltipParams: () => void;
}) => {
  useEffect(() => {
    if (!mentionTooltipParams) return;
    if (!mentionTooltipParams.visible) return;

    const handleScroll = () => {
      if (mentionTooltipParams.visible) {
        resetMentionTooltipParams();
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [mentionTooltipParams, resetMentionTooltipParams]);
};
